import { Dispatch, Middleware } from 'redux'
import { setUserId } from '@snowplow/browser-tracker'

import { AppStore } from '../store'
import { OptimizelyEvent } from './optimizely/events'
import { signupSuccessEvent } from './snowplow/events'
import { SnowplowEvent } from './snowplow/events'
import * as Optimizely from './optimizely/optimizely'
import * as Snowplow from './snowplow/snowplow'

import { API_CALL_SUCCESS } from 'actions/actionConstants'
import { USER_LOGIN, USER_REGISTRATION } from 'actions/apiActionConstants'

export enum Tracker {
  optimizely = 'optimizely',
  snowplow = 'snowplow',
}

const eventSubscribers: Record<string, Tracker[]> = {
  'add-to-cart-tour': [Tracker.optimizely],
  'add-to-cart-accommodation': [Tracker.optimizely],
  'add-to-cart': [Tracker.optimizely],
  'agent-insurance-upsell-event': [Tracker.snowplow],
  'cart-event': [Tracker.snowplow],
  'click-tpfm-carousel': [Tracker.optimizely],
  'click-tour-offer': [Tracker.optimizely],
  'client-event': [Tracker.snowplow],
  'dismissed-add-to-trip': [Tracker.snowplow],
  'experience-event': [Tracker.snowplow],
  'front-page-client-event': [Tracker.snowplow],
  'impression-event': [Tracker.snowplow],
  'log-auth': [Tracker.snowplow],
  'log-operational': [Tracker.snowplow],
  'log-registration': [Tracker.snowplow],
  'offer-click-accommodation': [Tracker.optimizely],
  'offer-event': [Tracker.snowplow],
  'offer-page-hot-lead-event': [Tracker.snowplow],
  'purchase-click': [Tracker.optimizely],
  'purchase-lpc-offer': [Tracker.optimizely],
  'purchase-tour-offer': [Tracker.optimizely],
  'save-to-trip': [Tracker.snowplow, Tracker.optimizely],
  'search-click-lpc-offer': [Tracker.optimizely],
  'search-click-bedbank-offer': [Tracker.optimizely],
  'search-map-click-offer': [Tracker.optimizely],
  'search-map-click-lpc-offer': [Tracker.optimizely],
  'search-map-click-flash-offer': [Tracker.optimizely],
  'search-map-click-bedbank-offer': [Tracker.optimizely],
  'search-map-pin-click-offer': [Tracker.optimizely],
  'search-map-pin-click-lpc-offer': [Tracker.optimizely],
  'search-map-pin-click-flash-offer': [Tracker.optimizely],
  'search-map-pin-click-bedbank-offer': [Tracker.optimizely],
  'search-click-offer': [Tracker.optimizely],
  'search-event': [Tracker.snowplow],
  'signup-success': [Tracker.optimizely],
  'upgrade-package': [Tracker.optimizely],
  'search-from-homepage': [Tracker.optimizely],
  'click-homepage-navbar': [Tracker.optimizely],
  'click-homepage-tile': [Tracker.optimizely],
  'click-offer-on-hotels-page': [Tracker.optimizely],
  'click-homepage-recommendation-tile': [Tracker.optimizely],
  'click-old-homepage-tile-including-recommendation': [Tracker.optimizely],
  checkout: [Tracker.optimizely],
  'view-map-page': [Tracker.optimizely],
  // to track about us click from the specific USP component (FrontPageUSPComponent)
  'usp-about-us-event': [Tracker.snowplow],

  // This is temporary and will be removed in the next few weeks as we aim to unify our analytics events
  'view-offer': [Tracker.optimizely],
  // Will be removed after experiment ends
  'click-calender-recommendation': [Tracker.optimizely],
  'signup-success-event': [Tracker.snowplow],
  'tour-upgrade-package': [Tracker.snowplow],
  'click-tour-trending-destinations': [Tracker.snowplow],
  'view-recommendation-on-lpc-page': [Tracker.optimizely],
  'click-recommendation-on-lpc-page': [Tracker.optimizely],
  'share-offer': [Tracker.optimizely],
}

export async function track(eventName: keyof typeof eventSubscribers, event: OptimizelyEvent | SnowplowEvent) {
  if (!eventSubscribers[eventName]) {
    throw new Error(`Event ${eventName} not supported`)
  }

  if (eventSubscribers[eventName].includes(Tracker.optimizely)) {
    Optimizely.track(event as OptimizelyEvent)
  }

  if (eventSubscribers[eventName].includes(Tracker.snowplow)) {
    Snowplow.track(event as SnowplowEvent)
  }
}

export function init(tracker: Tracker, userId?: string, attributes?: Record<string, any>, store?: AppStore) {
  if (tracker === Tracker.optimizely && userId && attributes && store) {
    Optimizely.init(userId, attributes, store)
  }
  else if (tracker === Tracker.snowplow) {
    Snowplow.init()
  }
}

export function updateContext(tracker: Tracker, key: string, value: string) {
  if (tracker === Tracker.snowplow) {
    Snowplow.updateContext(key, value)
  }
}

export const analyticsMiddleware: Middleware<{}, App.State, Dispatch<any>> = () => next => (action) => {
  // If it's receiving an action from another tab/window, don't track it
  if (action.supressBroadcast) return next(action)

  if (action.api != USER_LOGIN && action.api != USER_REGISTRATION) return next(action)
  if (action.type != API_CALL_SUCCESS) return next(action)

  const account = action.data.account as App.AuthAccount
  setUserId(account.memberId)

  if (action.data.isSignUp) track('signup-success-event', signupSuccessEvent(action.source))

  return next(action)
}
